import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, XIcon, CloudIcon } from 'lucide-react';

import { Box, HStack, Text, Spinner } from '../../index';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface SaveIndicatorProps {
  status: SaveStatus;
  lastSaved?: Date | null;
  errorMessage?: string;
  className?: string;
  showTimestamp?: boolean;
}

export function SaveIndicator({
  status,
  lastSaved,
  errorMessage,
  className = '',
  showTimestamp = true,
}: SaveIndicatorProps) {
  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return date.toLocaleString();
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'saving':
        return <Spinner size="sm" color="#0066CC" />;
      case 'saved':
        return <CheckIcon size={16} color="#10B981" />;
      case 'error':
        return <XIcon size={16} color="#EF4444" />;
      default:
        return <CloudIcon size={16} color="#6B7280" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'saving':
        return 'Saving...';
      case 'saved':
        return 'All changes saved';
      case 'error':
        return errorMessage || 'Save failed';
      default:
        return 'Auto-save enabled';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'saving':
        return '#0066CC';
      case 'saved':
        return '#10B981';
      case 'error':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={status}
        initial={{ opacity: 0, y: -5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -5 }}
        transition={{ duration: 0.2 }}
      >
        <HStack gap={2} className={className}>
          <Box display="flex" alignItems="center">
            {getStatusIcon()}
          </Box>
          <Text fontSize="sm" color={getStatusColor()} fontWeight="medium">
            {getStatusText()}
          </Text>
          {showTimestamp && lastSaved && status === 'saved' && (
            <Text fontSize="xs" color="#6B7280">
              ({formatTimestamp(lastSaved)})
            </Text>
          )}
        </HStack>
      </motion.div>
    </AnimatePresence>
  );
}
